import { Formik, useFormikContext } from "formik";
import { useNavigate } from "react-router";
import { PropsWithChildren, useEffect } from "react";

import AlertDialog from "../../../common/AlertDialog";
import useAddSolutionForm from "../../hooks/useAddSolutionForm";
import WriteableSolutionFormContainer from "../../form/components";
import routes from "../../../../routes";
import SingleSelect from "../../../common/SingleSelect";
import ProgrammeInfoContainer from "../../form/components/programme-info";
import {
  outcomesTouched,
  isUnitOfSaleSelected,
  isUnitOfSaleOutcomeFilledOut,
  mappedMethodologyField,
  interventionTypeOptions,
  assetTypeOptions,
} from "../../utils";
import { writeableSolutionFormDefaults } from "../form/defaultValues";
import { addSolutionFormSchema } from "../form/schema";
import { AddSolutionFormValues } from "../form/types";

export type AddSolutionFormFieldsProps = {
  submitLoading: boolean;
  handleCancel: () => void;
};

function AddSolutionFormFields({
  submitLoading,
  handleCancel,
}: AddSolutionFormFieldsProps) {
  const { values, isValid, setFieldTouched } =
    useFormikContext<AddSolutionFormValues>();
  const hasBiodiversityUnitOfSale = values.outcomes.some(
    (outcome) => outcome.unit === "Biodiversity" && outcome["unit-of-sale"],
  );

  useEffect(() => {
    if (!hasBiodiversityUnitOfSale) return;
    setFieldTouched("solution-vbc-website-display-name", true);
  }, [hasBiodiversityUnitOfSale, setFieldTouched]);

  const submitDisabled =
    !isValid ||
    (outcomesTouched(values) &&
      (!isUnitOfSaleSelected(values) || !isUnitOfSaleOutcomeFilledOut(values)));

  return (
    <WriteableSolutionFormContainer
      submitLoading={submitLoading}
      submitDisabled={submitDisabled}
      handleCancel={handleCancel}
      title="Add Project"
      actionsMarginBottom={2.5}
      assessmentMethodologyField={
        <SingleSelect
          options={mappedMethodologyField}
          name="assessment-methodology"
          label="Assessment Methodology"
          fieldPath="solution-assessment-methodology"
          warnInsteadOfError={false}
        />
      }
      interventionTypeField={
        <SingleSelect
          name="intervention-type"
          label="Intervention Type"
          options={interventionTypeOptions}
          fieldPath="solution-intervention-type"
          warnInsteadOfError={false}
        />
      }
      programmeInfoSection={
        <ProgrammeInfoContainer
          assetTypeField={
            <SingleSelect
              name="asset-type"
              label="Asset Type (Credit Type)"
              options={assetTypeOptions}
              fieldPath="programme-info.asset-type"
              warnInsteadOfError={false}
            />
          }
        />
      }
    />
  );
}

function AddSolutionForm() {
  const navigate = useNavigate();
  const {
    formSuccess,
    formError,
    formSubmittalLoading,
    handleSubmit,
    alert,
    setAlert,
  } = useAddSolutionForm();

  return (
    <>
      <AlertDialog
        handleClose={() => {
          setAlert(null);
          if (formError) return;

          navigate(
            routes.solutions.view({ id: formSuccess?.data.solution.id }),
          );
        }}
        open={!!alert}
        title={`Add project ${alert?.type}`}
        content={alert?.message || ""}
      />
      <SolutionFormikWrapper handleSubmit={handleSubmit}>
        <AddSolutionFormFields
          submitLoading={formSubmittalLoading}
          handleCancel={() => navigate(-1)}
        />
      </SolutionFormikWrapper>
    </>
  );
}

export const SolutionFormikWrapper = ({
  children,
  handleSubmit,
  initialValues = writeableSolutionFormDefaults,
}: PropsWithChildren<{
  handleSubmit: (formData: AddSolutionFormValues) => void;
  initialValues?: AddSolutionFormValues;
}>) => (
  <Formik
    initialValues={initialValues}
    validationSchema={addSolutionFormSchema}
    onSubmit={(formData) => handleSubmit(formData)}
    validateOnMount
    initialTouched={{
      "solution-name": true,
      "solution-developer": true,
      "solution-assessment-methodology": true,
      "solution-intervention-type": true,
      "solution-short-description": true,
      "country-select": true,
      "solution-is-ready-for-sale": true,
      "solution-start-date": true,
      "solution-end-date": true,
      latitude: true,
      longitude: true,
      interventions: initialValues.interventions?.map(() => ({
        action: true,
        ecosystem: true,
      })),
      "internal-details": {
        "website-url": true,
      },
      "programme-info": {
        "asset-type": true,
        "carbon-crediting-programme": true,
      },
      "risk-monitoring": {
        "is-risk-alert-enabled": true,
        "buffer-value-km": true,
        "geospatial-file": true,
      },
    }}
  >
    {children}
  </Formik>
);

export default AddSolutionForm;
